import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { incidentService } from "../../services/IncidentService"
import useUser from "../../hooks/useUser"
import LocationSummaryCard from "../../components/dashboard/LocationSummaryCard"
import ConstructionDashboardPayroll from "../../components/construction/ConstructionDashboardPayroll"
import { CircularProgress } from "@mui/material"
import { Incident } from "../../Interfaces"
import Searchbar from "../../components/tables/common/Searchbar"
import { usePublicSiteId } from "../../contexts/PublicSiteContext"
import { isConstructionWalk } from "../../utils/publicSite"
import { useSelectedCompany } from "../../context/companyProvider"

interface LocationWithIncidents {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  incidents: Incident[];
  walkReportStats: {
    last72Hours: number;
    lastWeek: number;
  };
}

export default function Dashboard() {
  const { id } = useUser()
  const siteId = usePublicSiteId()
  const construction = isConstructionWalk(siteId)
  const { selectedCompany } = useSelectedCompany()
  const [searchTerm, setSearchTerm] = useState("")
  const { data: locations, isLoading, error } = useQuery<LocationWithIncidents[]>(
    ["userLocations", id, selectedCompany?.id],
    () => incidentService.getUserLocations(id, selectedCompany?.id),
    {
      enabled: !!id && !construction,
    },
  )

  const filteredLocations = locations?.filter(location =>
    location.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (construction) {
    return (
      <div className="w-full min-h-full flex justify-center">
        <div className="w-full max-w-6xl p-4">
          <ConstructionDashboardPayroll />
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full flex justify-center">
        <div className="w-full h-fit">
          <h1 className="text-3xl font-bold text-center p-8">Error loading dashboard data</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full min-h-full flex justify-center">
      <div className="w-full max-w-6xl p-4">
        <div className="flex align-middle p-2 max-w-full justify-between">
          <div className="flex align-middle p-2 max-w-full items-center">
            <h2 className="pr-2">Dashboard</h2>
            <div className="bg-secondary-extra-light shadow-md rounded-md flex justify-center items-center w-7 h-7 min-w-fit">
              <Searchbar onSearch={setSearchTerm} />
            </div>
          </div>
        </div>
        <div className="space-y-6">
          {filteredLocations?.map((location: LocationWithIncidents) => (
            <LocationSummaryCard key={location.id} location={location} />
          ))}
        </div>
      </div>
    </div>
  )
}
